import * as T from 'three';

/**
 * View-blended tree impostors. Each tree type is an atlas of N horizontal views rendered in
 * Blender (albedo with alpha, plus camera-space normals). A camera-facing card picks and blends
 * the two views nearest the viewing direction, lights them with the baked normals, and casts a
 * silhouette shadow by facing the light in the shadow pass.
 */
export interface ImpostorSpec { id: string; albedo: T.Texture; normal: T.Texture; views: number; width: number; height: number }
export interface TreeInstance { x: number; y: number; z: number; scale: number; yaw: number; tint?: T.Color }

const VERTEX_PARS = /* glsl */`
uniform float uViews; uniform float uTime;
varying vec2 vUv0; varying vec2 vUv1; varying float vBlend; varying vec3 vRightW; varying vec3 vFacingW; varying float vH;`;
// Billboard about the instance's vertical axis, then choose atlas views from the azimuth.
const VERTEX_BILLBOARD = /* glsl */`
  vec3 iOrigin = ( modelMatrix * instanceMatrix * vec4( 0.0, 0.0, 0.0, 1.0 ) ).xyz;
  float iScale = length( ( instanceMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz );
  vec3 iFwd = normalize( ( instanceMatrix * vec4( 0.0, 0.0, 1.0, 0.0 ) ).xyz );
  vec2 toCam = cameraPosition.xz - iOrigin.xz; float toLen = length( toCam ); toCam = toLen > 1e-4 ? toCam / toLen : vec2( 0.0, 1.0 );
  vec3 facing = vec3( toCam.x, 0.0, toCam.y ); vec3 rightW = vec3( facing.z, 0.0, - facing.x );
  float sway = sin( uTime * 0.9 + iOrigin.x * 0.07 + iOrigin.z * 0.05 ) * 0.012 * position.y * position.y / max( iScale, 0.01 );
  vec3 billboarded = iOrigin + rightW * ( position.x * iScale + sway ) + vec3( 0.0, position.y * iScale, 0.0 );
  // Azimuth of the camera in the tree's own frame: view k was rendered from direction (sin θk, 0, cos θk).
  float az = atan( facing.x, facing.z ) - atan( iFwd.x, iFwd.z );
  float k = fract( az / 6.2831853 ) * uViews;
  float k0 = floor( k ); float k1 = mod( k0 + 1.0, uViews );
  vBlend = k - k0;
  vUv0 = vec2( ( k0 + uv.x ) / uViews, uv.y ); vUv1 = vec2( ( k1 + uv.x ) / uViews, uv.y );
  vRightW = rightW; vFacingW = facing; vH = uv.y;`;

function patchVertex(shader: { vertexShader: string }) {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `#include <common>\n${VERTEX_PARS}`)
    .replace('#include <project_vertex>', `${VERTEX_BILLBOARD}\n vec4 mvPosition = viewMatrix * vec4( billboarded, 1.0 ); gl_Position = projectionMatrix * mvPosition;`)
    .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\n#if defined( USE_SHADOWMAP ) || defined( USE_ENVMAP )\n worldPosition = vec4( billboarded, 1.0 );\n#endif');
}

export function impostorMaterial(spec: ImpostorSpec, time: { value: number }) {
  const uniforms = { tAlbedoAtlas: { value: spec.albedo }, tNormalAtlas: { value: spec.normal }, uViews: { value: spec.views }, uTime: time };
  const material = new T.MeshStandardMaterial({ color: '#ffffff', roughness: .85, metalness: 0, alphaTest: .5, side: T.DoubleSide });
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms); patchVertex(shader);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform sampler2D tAlbedoAtlas; uniform sampler2D tNormalAtlas;\nvarying vec2 vUv0; varying vec2 vUv1; varying float vBlend; varying vec3 vRightW; varying vec3 vFacingW; varying float vH;`)
      .replace('#include <map_fragment>', `
        vec4 a0 = texture2D( tAlbedoAtlas, vUv0 ), a1 = texture2D( tAlbedoAtlas, vUv1 );
        float w1 = smoothstep( 0.2, 0.8, vBlend );
        vec4 alb = mix( a0, a1, w1 );
        // Keep the silhouette of the dominant view crisp instead of ghosting both.
        alb.a = mix( a0.a, a1.a, step( 0.5, w1 ) );
        diffuseColor *= alb;`)
      .replace('#include <normal_fragment_maps>', `
        vec3 n0 = texture2D( tNormalAtlas, vUv0 ).xyz * 2.0 - 1.0, n1 = texture2D( tNormalAtlas, vUv1 ).xyz * 2.0 - 1.0;
        vec3 nc = normalize( mix( n0, n1, smoothstep( 0.2, 0.8, vBlend ) ) );
        vec3 nW = normalize( vRightW * nc.x + vec3( 0.0, 1.0, 0.0 ) * nc.y + vFacingW * nc.z );
        // Soften toward up so canopies read as volumes rather than flat cards.
        nW = normalize( mix( nW, vec3( 0.0, 1.0, 0.0 ), 0.18 ) );
        normal = normalize( ( viewMatrix * vec4( nW, 0.0 ) ).xyz );`);
  };
  material.customProgramCacheKey = () => 'tq-impostor';
  const depth = new T.MeshDepthMaterial({ depthPacking: T.RGBADepthPacking, alphaTest: .5 });
  depth.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms); patchVertex(shader);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D tAlbedoAtlas; varying vec2 vUv0; varying vec2 vUv1; varying float vBlend;')
      .replace('#include <alphatest_fragment>', 'if ( texture2D( tAlbedoAtlas, vBlend < 0.5 ? vUv0 : vUv1 ).a < 0.5 ) discard;');
  };
  depth.customProgramCacheKey = () => 'tq-impostor-depth';
  return { material, depth };
}

/** One draw call per tree type. The quad's base sits on the ground at the instance origin. */
export function makeForest(spec: ImpostorSpec, trees: TreeInstance[], time: { value: number }) {
  const geometry = new T.PlaneGeometry(spec.width, spec.height, 1, 4); geometry.translate(0, spec.height / 2, 0);
  const { material, depth } = impostorMaterial(spec, time);
  const mesh = new T.InstancedMesh(geometry, material, trees.length); mesh.name = `forest-${spec.id}`;
  const m = new T.Matrix4(), q = new T.Quaternion(), up = new T.Vector3(0, 1, 0), s = new T.Vector3(), p = new T.Vector3();
  trees.forEach((t, i) => {
    q.setFromAxisAngle(up, t.yaw); s.setScalar(t.scale); p.set(t.x, t.y, t.z); m.compose(p, q, s); mesh.setMatrixAt(i, m);
    mesh.setColorAt(i, t.tint ?? new T.Color(1, 1, 1));
  });
  mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.customDepthMaterial = depth; mesh.castShadow = true; mesh.receiveShadow = true;
  // Billboards extend beyond the static quad bounds; pad the culling sphere.
  mesh.computeBoundingSphere(); if (mesh.boundingSphere) mesh.boundingSphere.radius += spec.width * 2;
  return mesh;
}
